import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Actions,
  ButtonsContainer,
  Row,
  StyledButton,
  TestButton,
  TestVoiceContainer,
  VoiceButton,
  VoiceNameContainer,
  VoiceNameContent,
  VoiceUploadContainer,
} from '../../../../components/onboarding/InstantVoiceUpload/InstantVoiceUpload.styles';
import { GenerateButton } from '../EditInfluencerPage.styles';
import { Text14Light, Text14Regular, Text16Regular } from '../../../../components/utils/Text/Text.styles';
import { ErrorContainer, MediaRecorderComponent, UploadMediaButton } from '../../../../components';
import Input from '../../../../components/utils/inputs/Input/Input';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import { ReactComponent as PlayIcon } from '../../../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../../../assets/icons/pause-circle.svg';
import { ReactComponent as PlayWhiteIcon } from '../../../../assets/icons/play-white.svg';
import { selectChatterActionsPending } from '../../../../store/slices/chatter/slice';
import {
  saveInfluencerVoice,
  testInfluencerVoice,
  uploadInfluencerVoice,
} from '../../../../store/slices/chatter/asyncThunks';
import { UPLOAD_FILE_CONSTRAINTS, UPLOAD_MEDIA_FILE_ERROR_TYPE } from '../../../../utils/constants';
import { notifyError, notifyInfo } from '../../../../utils/notify';

// Same options as instant cloning during onboarding
const VOICES = [
  { title: 'Voice 1', stability: 0 },
  { title: 'Voice 2', stability: 0.5 },
  { title: 'Voice 3', stability: 1 },
];

const ERROR_MESSAGES = {
  [UPLOAD_MEDIA_FILE_ERROR_TYPE.FILE_SIZE]: `File size is too large (max ${UPLOAD_FILE_CONSTRAINTS.AUDIO.MAX_SIZE_IN_MB}MB)`,
  [UPLOAD_MEDIA_FILE_ERROR_TYPE.FILE_TYPE]: `Invalid file type (allowed: ${UPLOAD_FILE_CONSTRAINTS.AUDIO.ALLOWED_EXTENSIONS_TEXT})`,
  [UPLOAD_MEDIA_FILE_ERROR_TYPE.AUDIO_DURATION]: `Audio duration is less than ${UPLOAD_FILE_CONSTRAINTS.AUDIO.MIN_DURATION_IN_SECONDS} seconds`,
};

const VoiceSection = ({ influencer }) => {
  const dispatch = useDispatch();
  const {
    uploadInfluencerVoice: uploadPending,
    testInfluencerVoice: testPending,
    saveInfluencerVoice: savePending,
  } = useSelector(selectChatterActionsPending);
  const audioRef = useRef(null);

  const [audio, setAudio] = useState(null);
  const [errors, setErrors] = useState([]);
  const [voiceId, setVoiceId] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(VOICES[0]);
  const [message, setMessage] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVoiceSaved, setIsVoiceSaved] = useState(false);

  useEffect(() => () => audioRef.current?.pause(), []);

  const onPauseVoice = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const onPlayVoice = () => {
    if (!audio) return;
    if (!audioRef.current) {
      const player = new Audio(audio.url);
      audioRef.current = player;
      player.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
      };
    }
    audioRef.current.play();
    setIsPlaying(true);
  };

  const onDeleteVoice = () => {
    onPauseVoice();
    audioRef.current = null;
    setAudio(null);
    setErrors([]);
    setVoiceId(null);
  };

  const onUpload = () => {
    dispatch(uploadInfluencerVoice({ influencerId: influencer.id, audio }))
      .unwrap()
      .then(({ voiceId }) => {
        setVoiceId(voiceId);
        notifyInfo('Audio uploaded successfully');
      })
      .catch(err => notifyError(err.message));
  };

  const onTest = () => {
    if (!message.trim()) {
      notifyError('Please enter text');
      return;
    }
    dispatch(testInfluencerVoice({ influencerId: influencer.id, voiceId, stability: selectedVoice.stability, message }))
      .unwrap()
      .then(({ speech }) => {
        const audioBlob = new Blob([new Uint8Array(speech.data)], { type: 'audio/mp3' });
        new Audio(URL.createObjectURL(audioBlob)).play();
      })
      .catch(err => notifyError(err.message));
  };

  const onSave = () => {
    dispatch(saveInfluencerVoice({ influencerId: influencer.id, voiceId, stability: selectedVoice.stability }))
      .unwrap()
      .then(() => {
        onPauseVoice();
        setIsVoiceSaved(true);
        notifyInfo('Voice saved successfully!');
      })
      .catch(err => notifyError(err.message));
  };

  if (isVoiceSaved) {
    return <Text14Regular>Voice has been cloned and saved.</Text14Regular>;
  }

  if (influencer.hasVoice) {
    return <Text14Regular>Voice is already set for this creator.</Text14Regular>;
  }

  return (
    <>
      <Text14Regular>
        Record the creator's voice or upload an audio file (at least{' '}
        {UPLOAD_FILE_CONSTRAINTS.AUDIO.MIN_DURATION_IN_SECONDS} seconds). It will be cloned so the creator can send
        voice notes.
      </Text14Regular>
      {audio ? (
        <VoiceUploadContainer>
          <VoiceNameContainer>
            <VoiceNameContent>
              <Text14Light>{audio.file.name || 'Uploaded Audio'}</Text14Light>
              <Actions>
                {isPlaying ? (
                  <PauseIcon onClick={onPauseVoice} width={20} height={20} color="#1672BA" />
                ) : (
                  <PlayIcon onClick={onPlayVoice} width={20} height={20} color="#1672BA" />
                )}
                {!uploadPending && !savePending && (
                  <CloseIcon onClick={onDeleteVoice} width={20} height={20} color="#1672BA" />
                )}
              </Actions>
            </VoiceNameContent>
            {errors[0] && <ErrorContainer errorText={ERROR_MESSAGES[errors[0].type]} />}
          </VoiceNameContainer>
          {!voiceId && (
            <StyledButton title="Upload" onClick={onUpload} isLoading={uploadPending} disabled={!!errors[0]} />
          )}
        </VoiceUploadContainer>
      ) : (
        <>
          <UploadMediaButton
            type={UPLOAD_FILE_CONSTRAINTS.TYPE.AUDIO}
            onChange={files => {
              setAudio(files[0] || null);
              audioRef.current = null;
            }}
            setError={setErrors}
          />
          <MediaRecorderComponent
            setRecordedVoice={file => {
              setAudio(file);
              if (file.duration < UPLOAD_FILE_CONSTRAINTS.AUDIO.MIN_DURATION_IN_SECONDS) {
                setErrors([{ type: UPLOAD_MEDIA_FILE_ERROR_TYPE.AUDIO_DURATION }]);
              }
              audioRef.current = null;
            }}
            recordedVoice={audio}
          />
        </>
      )}
      {voiceId && (
        <TestVoiceContainer>
          <Text16Regular>Select Voice</Text16Regular>
          <ButtonsContainer>
            {VOICES.map(voice => (
              <VoiceButton
                key={voice.title}
                type="button"
                $selected={selectedVoice.title === voice.title}
                onClick={() => setSelectedVoice(voice)}
              >
                {voice.title}
              </VoiceButton>
            ))}
          </ButtonsContainer>
          <Row>
            <Input
              value={message}
              onChange={e => setMessage(e.target.value)}
              label="Enter text to test the voice"
              placeholder="Enter text here"
            />
            <TestButton title="Play" icon={<PlayWhiteIcon />} isLoading={testPending} onClick={onTest} />
          </Row>
        </TestVoiceContainer>
      )}
      <GenerateButton
        title={voiceId ? `Save ${selectedVoice.title}` : 'Save'}
        onClick={onSave}
        disabled={!voiceId}
        isLoading={savePending}
        type="button"
      />
    </>
  );
};

export default VoiceSection;
